// Segmented one-time-code input — a row of single-character glass boxes
// (the "_ _ _ _ _ _" verification-code pattern). Numeric only, auto-advances
// on type, backspaces to the previous box, and accepts a pasted full code.
//
// Controlled: `value` is the concatenated code string, `onChange` receives the
// new string. Kept hole-free (always a compact prefix) so box i is filled iff
// i < value.length — no shifting/misalignment when editing.
//
// Ported from catchup-admin-web's library (same ci- prefix / glass tokens) so
// code entry looks identical across the apps.

import { useRef } from "react";

export interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Number of boxes / max code length. Default 6. */
  length?: number;
  /** Fires when Enter is pressed inside any box (submit affordance). */
  onSubmit?: () => void;
  /** Renders the boxes in an error state (red ring). */
  error?: boolean;
  /** Focus the first box on mount. */
  autoFocus?: boolean;
}

const CodeInput = ({
  value,
  onChange,
  length = 6,
  onSubmit,
  error,
  autoFocus,
}: CodeInputProps) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const focusAt = (i: number) =>
    refs.current[Math.max(0, Math.min(i, length - 1))]?.focus();

  const handleChange = (i: number, raw: string) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) return; // empty change (deletion) is handled in keydown
    let next: string;
    if (digits.length === 1) {
      // replace char at i, preserving the tail; appends when i >= value.length
      next = (value.slice(0, i) + digits + value.slice(i + 1)).slice(0, length);
      onChange(next);
      focusAt(i + 1);
    } else {
      // multi-char (paste landed inside a box): fill from i
      next = (value.slice(0, i) + digits).slice(0, length);
      onChange(next);
      focusAt(next.length);
    }
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSubmit?.();
    } else if (e.key === "Backspace") {
      e.preventDefault();
      if (i < value.length && value[i]) {
        onChange(value.slice(0, i) + value.slice(i + 1));
        focusAt(i);
      } else if (value.length > 0) {
        const next = value.slice(0, -1);
        onChange(next);
        focusAt(next.length);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const digits = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!digits) return;
    e.preventDefault();
    onChange(digits);
    focusAt(digits.length);
  };

  return (
    <div className={`ci-wrap${error ? " is-error" : ""}`} onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="ci-box"
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={`${i + 1}`}
          value={value[i] || ""}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
      <style>{`
        .ci-wrap {
          display: grid;
          grid-template-columns: repeat(${length}, 1fr);
          gap: 8px;
          width: 100%;
        }
        .ci-box {
          width: 100%;
          height: 52px;
          text-align: center;
          font-family: var(--font-sans);
          font-size: 22px;
          font-weight: 700;
          color: var(--text-primary);
          border-radius: 12px;
          border: 1px solid var(--glass-flat-border);
          background: var(--glass-flat-bg);
          box-shadow: var(--glass-flat-shadow);
          outline: none;
          caret-color: var(--brand);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          -moz-appearance: textfield;
        }
        .ci-box::-webkit-outer-spin-button,
        .ci-box::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ci-box:focus {
          border-color: var(--brand);
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.9), 0 0 0 3px rgba(84,187,203,0.2);
        }
        .ci-wrap.is-error .ci-box {
          border-color: var(--color-error-border);
        }
        .ci-wrap.is-error .ci-box:focus {
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.9), 0 0 0 3px rgba(226,91,91,0.18);
        }
        @media (max-width: 420px) {
          .ci-box { height: 46px; font-size: 19px; }
          .ci-wrap { gap: 6px; }
        }
      `}</style>
    </div>
  );
};

export default CodeInput;
